import { supabase } from "~/integrations/supabase/client";
import type {
  Account,
  AccountFilters,
  AccountFormData,
  AccountType,
} from "~/types/contabilidad";

export interface AccountImportData {
  code: string;
  name: string;
  account_type: AccountType;
  is_active: boolean;
}

export interface AccountImportResult {
  created: number;
  skipped: number;
  errors: Array<{ code: string; error: string }>;
}

export const accountService = {
  async getAll(filters?: AccountFilters): Promise<Account[]> {
    let query = supabase
      .from("account")
      .select("*, company:company_id(*)")
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.account_type) {
      query = query.eq("account_type", filters.account_type);
    }

    if (filters?.is_active !== undefined) {
      query = query.eq("is_active", filters.is_active);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query.order("code", { ascending: true });

    if (error) throw error;
    return data as Account[];
  },

  async getByCompany(companyId: string): Promise<Account[]> {
    const { data, error } = await supabase
      .from("account")
      .select("*")
      .eq("company_id", companyId)
      .eq("is_deleted", false)
      .eq("is_active", true)
      .order("code", { ascending: true });

    if (error) throw error;
    return data as Account[];
  },

  async getById(id: string): Promise<Account> {
    const { data, error } = await supabase
      .from("account")
      .select("*, company:company_id(*)")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as Account;
  },

  async create(formData: AccountFormData): Promise<Account> {
    const { data, error } = await supabase
      .from("account")
      .insert({
        ...formData,
        is_active: formData.is_active ?? true,
      })
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Account;
  },

  async update(
    id: string,
    formData: Partial<AccountFormData>
  ): Promise<Account> {
    const { data, error } = await supabase
      .from("account")
      .update(formData)
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Account;
  },

  async toggleActive(id: string, isActive: boolean): Promise<Account> {
    const { data, error } = await supabase
      .from("account")
      .update({ is_active: isActive })
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as Account;
  },

  async delete(id: string): Promise<void> {
    // Soft delete using RPC function to validate no published movements
    const { error } = await supabase.rpc("soft_delete_account", {
      account_id: id,
    });

    if (error) throw error;
  },

  /**
   * Create multiple accounts in batch (create-only mode)
   * Skips accounts whose codes already exist
   */
  async createBatch(
    companyId: string,
    accounts: AccountImportData[]
  ): Promise<AccountImportResult> {
    const result: AccountImportResult = {
      created: 0,
      skipped: 0,
      errors: [],
    };

    if (accounts.length === 0) {
      return result;
    }

    // Get existing account codes for this company
    const { data: existingAccounts, error: fetchError } = await supabase
      .from("account")
      .select("code")
      .eq("company_id", companyId)
      .eq("is_deleted", false);

    if (fetchError) {
      throw fetchError;
    }

    const existingCodes = new Set(
      (existingAccounts || []).map((acc) => acc.code.toLowerCase())
    );

    // Filter out accounts that already exist
    const accountsToCreate = accounts.filter((acc) => {
      if (existingCodes.has(acc.code.toLowerCase())) {
        result.skipped++;
        return false;
      }
      return true;
    });

    if (accountsToCreate.length === 0) {
      return result;
    }

    // Process in batches of 50
    const BATCH_SIZE = 50;
    for (let i = 0; i < accountsToCreate.length; i += BATCH_SIZE) {
      const batch = accountsToCreate.slice(i, i + BATCH_SIZE);

      const insertData = batch.map((acc) => ({
        company_id: companyId,
        code: acc.code,
        name: acc.name,
        account_type: acc.account_type,
        is_active: acc.is_active,
      }));

      const { data, error } = await supabase
        .from("account")
        .insert(insertData)
        .select();

      if (error) {
        // If batch fails, try individual inserts to identify problematic records
        for (const acc of batch) {
          const { error: singleError } = await supabase
            .from("account")
            .insert({
              company_id: companyId,
              code: acc.code,
              name: acc.name,
              account_type: acc.account_type,
              is_active: acc.is_active,
            })
            .select()
            .single();

          if (singleError) {
            result.errors.push({
              code: acc.code,
              error: singleError.message,
            });
          } else {
            result.created++;
          }
        }
      } else {
        result.created += data?.length ?? 0;
      }
    }

    return result;
  },
};
